import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import type { MultiSelectOption, SelectOption } from '@rtkwlf/fenrir-react';
import { Button, HStack, Primitive, sizes, Text } from '@rtkwlf/fenrir-react';
import type { Ticket } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import type { ItemType } from '@packages/components/dropdownSelect/MultiSelector';
import { FilterMenu } from '@packages/components/filter/FilterMenu';
import { SearchInput } from '@packages/components/filter/SearchInput';
import { PageToolbar } from '@packages/components/page/page-toolbar';
import { Wrapper } from '@packages/components/wrapper/Wrapper';
import { PagePlain } from '@packages/fenrir/pages/page-plain';
import { TicketStatusOptions } from '@packages/legacy/ticket-status';

import { useListTickets } from '../../hooks/use-list-tickets/use-list-tickets';
import { TicketTable } from '../ticketTable/TicketTable';

import type { TicketFiltersObject } from './filter/ticketFilters.constants';
import {
  getLastUpdatedFromQuery,
  getTicketFilters,
  getValidateFilters,
  TicketFiltersEnum,
  ticketInitialFilter,
  TicketLastUpdatedOptions,
  ticketLastUpdatedOrdered,
} from './filter/ticketFilters.constants';
import { useTicketListColumns } from './hooks/use-ticket-list-columns';
import type { TicketDateRangeType } from './ticketList.utils';
import { getTicketDateRange } from './ticketList.utils';
import { TicketListHeader } from './TicketListHeader';

const isGreater = (
  a: TicketLastUpdatedOptions,
  b: TicketLastUpdatedOptions,
): boolean =>
  ticketLastUpdatedOrdered.indexOf(a) > ticketLastUpdatedOrdered.indexOf(b);

export const TicketList: React.FC = () => {
  const { t } = useTranslation();
  const filters = useRef(getTicketFilters(t)).current;

  const { search } = useLocation();

  const [query, setQuery] = useQueryParams({
    lastUpdated: StringParam,
    search: StringParam,
    status: StringParam,
    type: StringParam,
  });

  const [filterObj, setFilterObj] = useState<TicketFiltersObject>(() =>
    getValidateFilters(search, filters),
  );

  const columns = useTicketListColumns();

  const [statusFilter, setStatusFilter] = useState(
    query.status ?? ticketInitialFilter.status,
  );

  const maxLastUpdated = useRef<TicketLastUpdatedOptions>(
    getLastUpdatedFromQuery(
      query.lastUpdated?.toString() || ticketInitialFilter.lastUpdated,
    ),
  );
  const [ticketDateRange, setTicketDateRange] = useState<TicketDateRangeType>(
    getTicketDateRange(maxLastUpdated.current),
  );
  const [filteredData, setFilteredData] = useState<Required<Ticket>[]>();

  // TODO: review the type of value and verify the logic.
  const handleLastUpdatedChange = useCallback(
    (value: TicketLastUpdatedOptions) => {
      if (isGreater(value, maxLastUpdated.current)) {
        if (value !== maxLastUpdated.current) {
          setTicketDateRange(getTicketDateRange(value));
        }

        maxLastUpdated.current = value;
      }
    },
    [maxLastUpdated],
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      setStatusFilter(value);
      setTicketDateRange(getTicketDateRange(maxLastUpdated.current));
    },
    [setStatusFilter],
  );

  const handleFilterChange = useCallback(
    (field: keyof TicketFiltersObject, value: any) => {
      setFilterObj((prevObj) => {
        const newObj = { ...prevObj };
        if (value) {
          newObj[field] = value;
        } else {
          // eslint-disable-next-line prefer-reflect -- FIXME
          delete newObj[field];
        }

        if (field === TicketFiltersEnum.Status) {
          const newValue = value || TicketStatusOptions.All;
          newObj[field] = newValue;
          handleStatusChange(newValue);
        }

        if (field === TicketFiltersEnum.LastUpdated) {
          const newValue = value || TicketLastUpdatedOptions.Anytime;
          newObj[field] = newValue;
          handleLastUpdatedChange(newValue);
        }

        return newObj;
      });
    },
    [handleLastUpdatedChange, handleStatusChange],
  );

  const initialLoad = useRef(true);
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    const queryObj: TicketFiltersObject = {
      ...filterObj,
      lastUpdated: undefined,
      status: undefined,
    };

    if (filterObj.status !== ticketInitialFilter.status) {
      queryObj.status = filterObj.status;
    }

    if (filterObj.lastUpdated !== ticketInitialFilter.lastUpdated) {
      queryObj.lastUpdated = filterObj.lastUpdated;
    }

    setQuery(queryObj, 'push');
  }, [filterObj, handleLastUpdatedChange, handleStatusChange, setQuery]);

  const [showFilter, setShowFilter] = useState(true);

  const toggleFilter = () => {
    setShowFilter((prevState) => !prevState);
  };

  const resetAll = () => {
    // remove all the filter obj and setting initial filter.
    handleLastUpdatedChange(ticketInitialFilter.lastUpdated);
    setFilterObj(ticketInitialFilter);
  };

  const clearFilters = useCallback(() => {
    handleLastUpdatedChange(TicketLastUpdatedOptions.Anytime);
    setFilterObj({
      lastUpdated: TicketLastUpdatedOptions.Anytime,
      status: TicketStatusOptions.All,
    });
  }, [handleLastUpdatedChange]);

  const getInitialOption = useCallback(
    (options: ItemType[], currentValue: string | undefined) => {
      if (!currentValue) {
        return [];
      }
      const option = options.find(({ value }) => value === currentValue);
      return option ? [option] : [];
    },
    [],
  );

  const { isPending, isError, error, fetched, isFetching, ticketListQueryKey } =
    useListTickets({
      ...ticketDateRange,
      statusFilter,
    });

  const handleSearchChange = useCallback(
    (
      _field: string,
      _changes: SelectOption | MultiSelectOption[] | null,
      _isSingleSelect?: boolean | undefined,
      searchInputValue?: string | undefined,
    ) => {
      handleFilterChange('search', searchInputValue);
    },
    [handleFilterChange],
  );

  return (
    <>
      <PageToolbar.Root>
        <PageToolbar.RequestHelp />
      </PageToolbar.Root>
      <Wrapper
        wrapperStyle={css({
          marginTop: sizes['1.5rem'],
          overflow: 'visible',
        })}
      >
        <TicketListHeader
          showFilter={showFilter}
          toggleFilter={toggleFilter}
          isLoading={isFetching}
          filteredData={filteredData}
        />

        <HStack
          // Note: `display` property needs to be inside 'style' to pass its related tests.
          style={{ display: showFilter ? 'block' : 'none' }}
          backgroundColor="widgetSecondary"
          borderRadius="default"
          marginX="large"
        >
          <HStack display="flex" padding="default">
            <HStack flexWrap="wrap" alignItems="flex-end" zIndex={2}>
              <SearchInput
                value={filterObj.search ?? ''}
                labelPaddingBottom="small"
                handleChange={handleSearchChange}
              />

              {filters.map(
                ({
                  field,
                  label,
                  options,
                  placeholderKey,
                }): React.ReactElement => (
                  <FilterMenu
                    key={field}
                    isSingleSelect
                    // This is used as display! Must localize.
                    field={label}
                    values={[]}
                    options={options}
                    styles={{ multiSelectInputWrapStyle: css({}) }}
                    handleChange={(_field, changes) => {
                      const selectedItem = changes as SelectOption;
                      handleFilterChange(
                        field,
                        changes ? selectedItem.value : null,
                      );
                    }}
                    initialSelection={{}}
                    initialSelectedOption={getInitialOption(
                      options,
                      filterObj[field],
                    )}
                    placeholderKey={placeholderKey}
                  />
                ),
              )}
            </HStack>

            <HStack marginTop="small" alignSelf="flex-end">
              <Button
                variant="tertiary"
                onClick={resetAll}
                isDisabled={filterObj === ticketInitialFilter}
              >
                {t('components:filter.resetFilters')}
              </Button>
            </HStack>
          </HStack>
        </HStack>

        <Primitive.div width="full" marginY="default" paddingBottom="default">
          <TicketTable
            maxRowsPerPage={0}
            showPagination={false}
            columns={columns}
            filters={filterObj}
            isLoading={isPending}
            isError={isError}
            responseError={error}
            noData={
              <HStack
                width="full"
                xAlign="center"
                padding="large"
                wrap="wrap"
                gap="xsmall"
                alignItems="stretch"
              >
                <Text>{t('ticket:noResults.noMatch')}</Text>
                <Button variant="link" onClick={clearFilters}>
                  {t('ticket:noResults.clearFilters')}
                </Button>
                <Text>{t('ticket:noResults.tryAgain')}</Text>
              </HStack>
            }
            fetched={fetched}
            readingPane
            setFilteredData={setFilteredData}
            ticketListQueryKey={ticketListQueryKey}
          />
        </Primitive.div>
      </Wrapper>
    </>
  );
};

export const Component = () => {
  const { t } = useTranslation('ticket');
  return (
    <PagePlain title={t('allTickets')}>
      <TicketList />
    </PagePlain>
  );
};
